/* eslint-disable no-param-reassign */
import { createSlice } from '@reduxjs/toolkit';
import type { MyDailyMissionsResponse } from 'src/openapi';
import {
  claimDailyMissions,
  fetchDailyMissions,
  fetchLevelLadder,
} from '../actions/rewards';
import { setXp } from './auth';
import { RewardsState } from '../types/rewards';

const initialState: RewardsState = {
  missions: [],
  missionDate: null,
  claimableXp: 0,
  claimableCount: 0,
  isFetchingMissions: false,
  isClaiming: false,
  ladder: [],
  progress: null,
  promotion: null,
  welcomeCredits: 0,
  isFetchingLadder: false,
};

/**
 * The mission board, from either a fetch or a claim.
 *
 * Both endpoints return the whole board rather than a delta, so a claim lands
 * on server truth instead of a locally patched guess — a mission that another
 * tab or the mobile app already claimed shows as claimed.
 */
const applyBoard = (state: RewardsState, board: MyDailyMissionsResponse) => {
  state.missions = board.missions ?? [];
  state.missionDate = board.date ?? null;
  state.claimableXp = board.claimable_xp ?? 0;
  state.claimableCount = board.claimable_count ?? 0;
};

const rewardsSlice = createSlice({
  name: 'rewards',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(fetchDailyMissions.pending, (state) => {
        state.isFetchingMissions = true;
      })
      .addCase(fetchDailyMissions.fulfilled, (state, action) => {
        state.isFetchingMissions = false;
        applyBoard(state, action.payload);
      })
      .addCase(fetchDailyMissions.rejected, (state) => {
        state.isFetchingMissions = false;
      })
      .addCase(claimDailyMissions.pending, (state) => {
        state.isClaiming = true;
      })
      .addCase(claimDailyMissions.fulfilled, (state, action) => {
        state.isClaiming = false;
        applyBoard(state, action.payload);
      })
      .addCase(claimDailyMissions.rejected, (state) => {
        state.isClaiming = false;
      })
      .addCase(fetchLevelLadder.pending, (state) => {
        state.isFetchingLadder = true;
      })
      .addCase(fetchLevelLadder.fulfilled, (state, action) => {
        state.isFetchingLadder = false;
        state.ladder = action.payload.levels ?? [];
        state.progress = action.payload.progress ?? null;
        state.promotion = action.payload.promotion ?? null;
        state.welcomeCredits = action.payload.welcome_credits ?? 0;
      })
      .addCase(fetchLevelLadder.rejected, (state) => {
        state.isFetchingLadder = false;
      })
      .addCase(setXp, (state, action) => {
        const { xp, level, levelledUp } = action.payload;
        if (level) state.progress = { ...state.progress, ...level };
        // The rungs carry their own reached/claimed/current flags and an award
        // moves all three, so the hero rail alone is not enough — the track
        // beneath it would keep drawing the pre-award state until a remount.
        //
        // Patched from the socket rather than by re-fetching the ladder: the
        // push already names the rungs it *paid* (`levelled_up`), and the row
        // renders each rung's configured reward, so there is nothing here the
        // server would tell us that we don't have. Refetching after a claim
        // would also cover only claims, while XP arrives from a finished
        // multiplayer round or a topic test with this page open just the same.
        const paidNow = new Set(levelledUp ?? []);
        state.ladder = state.ladder.map((rung) => ({
          ...rung,
          is_reached: rung.is_reached || xp >= (rung.xp_threshold ?? 0),
          is_claimed:
            rung.is_claimed ||
            (rung.number != null && paidNow.has(rung.number)),
          // Only when the push carried a level, so a plain XP gain inside the
          // same band leaves the marker where it is rather than clearing it.
          is_current:
            level?.level == null
              ? rung.is_current
              : rung.number === level.level,
        }));
      });
  },
});

export default rewardsSlice.reducer;
